// Copied from kadret-admin's src/db/query.ts: named `:params` in the sql,
// checked against the object passed to `bind`.
import type {
  Database,
  SqlValue,
  Statement,
} from './database';

export const prepareQuery = (DB: Database, query: string) => {
  return new Query(DB, query);
};

type AcceptedParamTypes = SqlValue | boolean | string[];

const queryLogger = import.meta.env.DEV && import.meta.env.MODE !== 'test'
  ? console.debug
  : () => {};

const regex = /:([a-zA-Z0-9_\-]+)/g;

// a note body can run to megabytes; logging it whole stalls the devtools
const LOGGED_CHARS = 80;
const clip = (s: string) =>
  s.length > LOGGED_CHARS ? `${s.slice(0, LOGGED_CHARS)}… (${s.length})` : s;

export class Query {
  constructor(
    private readonly DB: Database,
    private readonly query: string,
  ) {}

  bind(paramsToBind: Record<string, AcceptedParamTypes> = {}): Statement {
    // SELECT id FROM table WHERE a = :x AND id = :id AND (SELECT b FROM u WHERE id = :id) IS :y
    // returns ['x', 'id', 'id', 'y'] - a name may appear more than once
    const paramNamesInQuery = Array.from(this.query.matchAll(regex))
      .map(([_, p]) => p);

    const expected = Array.from(new Set(paramNamesInQuery)).sort().join(',');
    const given = Object.keys(paramsToBind).sort().join(',');

    if (given !== expected) {
      throw new Error(
        'Incorrect parameter names for query.\n'
          + `Query: ${this.query}\n`
          + `Params: ${JSON.stringify(paramsToBind)}`,
      );
    }

    // one value per occurrence, in the order the question marks appear
    const paramValuesToBind = paramNamesInQuery
      .flatMap(p => paramsToBind[p]);

    // returns SELECT id FROM table WHERE a = ? AND b IN (?, ?)
    const questionMarkfied = this.query.replace(regex, (_, g) => {
      const value = paramsToBind[g];
      return Array.isArray(value) ? value.map(() => '?').join(',') : '?';
    });

    queryLogger(
      'Prepared query:\n'
        + this.query.replace(regex, (_: any, ...args: any) => {
          const v = paramsToBind[args[0]];
          return typeof v === 'string'
            ? `"${clip(v)}"`
            : Array.isArray(v)
            ? `"${v.map(String).join('","')}"`
            : String(v);
        }),
    );

    return this.DB
      .prepare(questionMarkfied)
      .bind(...paramValuesToBind);
  }

  bindAll(params: Record<string, AcceptedParamTypes>[]) {
    return params.map(p => this.bind(p));
  }
}
